/**
 * Program Execution WebSocket Service
 * Handles real-time program_execution messages and updates Redux activeRuns.
 */

import { AppDispatch } from '@/store/index';
import { WebSocketMessage } from './types';
import { WebSocketMessageRouter } from './messageRouter';
import { updateExecutionProgress } from '@/store/slices/programSlice';
import type { ExecutionRun } from '@/services/programApi';

export class ProgramExecutionWebSocketService {
  readonly name = 'program-execution-service';
  readonly version = '1.0.0';
  readonly dependencies: string[] = [];

  private router: WebSocketMessageRouter | null = null;
  private dispatch: AppDispatch | null = null;

  async initialize(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    this.router = router;
    this.dispatch = dispatch;

    for (const type of this.getMessageTypes()) {
      router.registerHandler({
        messageType: type,
        handler: this.handleMessage.bind(this),
        priority: 10,
        service: this.name,
      });
    }
  }

  async cleanup(): Promise<void> {
    if (this.router) {
      for (const type of this.getMessageTypes()) {
        this.router.unregisterHandler(type, this.name);
      }
    }
    this.router = null;
    this.dispatch = null;
  }

  getMessageTypes(): string[] {
    return ['program_execution'];
  }

  getHandlerPriority(): number {
    return 10;
  }

  getServiceInfo() {
    return {
      name: this.name,
      description: 'Routes program_execution updates into Redux activeRuns',
      version: this.version,
      author: 'Saqia',
      supportedMessageTypes: this.getMessageTypes(),
      dependencies: this.dependencies,
      priority: this.getHandlerPriority(),
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    if (!this.dispatch) return;

    const payload = (message.data as { payload?: Record<string, unknown> })?.payload
      ?? (message.data as Record<string, unknown>)
      ?? {};

    const programId = payload.program_id as number | undefined;
    const runId = payload.run_id as number | undefined;
    if (!programId || !runId) return;

    const update: Partial<ExecutionRun> & { id: number; program_id: number } = {
      id: runId,
      program_id: programId,
      run_status: payload.status as ExecutionRun['run_status'],
      current_step_index: payload.current_step as number | undefined,
      total_steps: payload.total_steps as number | undefined,
      execution_mode: payload.execution_mode as ExecutionRun['execution_mode'],
      farm_id: payload.farm_id as string | undefined,
      fert_phase: payload.fert_phase as ExecutionRun['fert_phase'],
      last_message: payload.message as string | undefined,
    };

    this.dispatch(updateExecutionProgress(update as ExecutionRun));
  }
}

export const programExecutionWebSocketService = new ProgramExecutionWebSocketService();
